// src/components/Navigationmenu.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BsCaretDownFill, BsCaretRightFill, BsInfoCircle, BsCheckCircle, BsHourglass, BsXCircle } from "react-icons/bs";
import { API_BASE_URL, API_INVENTORY } from '../../../helpers/urlHelper';

const menuItems = [
    { label: 'Doctors', path: '/doctors/list' },
    { label: 'Patients', path: '/doctors/list' },
    { label: 'Treatments', path: '/doctors/list' },
    { label: 'Health Centers', path: '/doctors/list' },
    { label: 'Medicines', path: '/doctors/list' },
    { label: 'Inventory', path: '/inventory/list' },
    { label: 'Prescriptions', path: '/doctors/list' },
    { label: 'Vaccination', path: '/doctors/list' },
    { label: 'Appointments', path: '/doctors/list' },
];

// 📊 Umbrales compartidos con la tabla
const THRESHOLD_IMMEDIATE = 50;
const THRESHOLD_LOW = 150;

function Navigationmenu({ inventoryData = [], filteredInventoryData = [] }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [showKPI, setShowKPI] = useState(true);
    
    const [kpiData, setKpiData] = useState({
        totalItems: 0,
        inStock: 0,
        lowStock: 0,
        needImmediate: 0,
    });

    const currentTabIndex = menuItems.findIndex(item => location.pathname === item.path);
    const currentTab = currentTabIndex !== -1 ? currentTabIndex : false;

    const handleChange = (event, newValue) => {
        navigate(menuItems[newValue].path);
    };

    // Ensure filteredInventoryData is used if it exists, otherwise fall back to all inventory
    const effectiveInventory = useMemo(() => {
        // If filteredInventoryData is provided and different from all inventory, use it
        if (filteredInventoryData && filteredInventoryData.length > 0 && 
            filteredInventoryData.length !== inventoryData.length) {
            return filteredInventoryData;
        }
        // Otherwise use the full dataset
        return inventoryData;
    }, [inventoryData, filteredInventoryData]);

    // Traer datos de inventario
    const fetchInventoryDataFromAPI = () => {
        fetch(`${API_BASE_URL}${API_INVENTORY}`)
            .then(res => res.json())
            .then(data => {
                computeAndSetKpis(data);
            })
            .catch(error => {
                console.error('Error al cargar datos para KPIs:', error);
            });
    };

    // Calculate all KPIs based on current effective inventory
    const kpis = useMemo(() => {
        // No data case
        if (!effectiveInventory || effectiveInventory.length === 0) {
            return {
                totalItems: 0,
                inStock: 0,
                lowStock: 0,
                needImmediate: 0
            };
        }

        const totalItems = effectiveInventory.length;
        // Need to add new stock immediately (< 50)
        const needImmediate = effectiveInventory.filter(item => item.stock < THRESHOLD_IMMEDIATE).length;
        // Low Stock (50 ≤ stock < 150)
        const lowStock = effectiveInventory.filter(
            item => item.stock >= THRESHOLD_IMMEDIATE && item.stock < THRESHOLD_LOW
        ).length;
        // In Stock (stock ≥ 150)
        const inStock = effectiveInventory.filter(item => item.stock >= THRESHOLD_LOW).length;

        return { totalItems, inStock, lowStock, needImmediate };
    }, [effectiveInventory]);

    // Calcular KPIs usando mismos umbrales que la tabla
    const computeAndSetKpis = data => {
        const totalItems = data.length;
        // Need to add new stock immediately (< 50)
        const needImmediate = data.filter(item => item.stock < THRESHOLD_IMMEDIATE).length;
        // Low Stock (50 ≤ stock < 150)
        const lowStock = data.filter(
            item => item.stock >= THRESHOLD_IMMEDIATE && item.stock < THRESHOLD_LOW
        ).length;
        // In Stock (stock ≥ 150)
        const inStock = data.filter(item => item.stock >= THRESHOLD_LOW).length;

        setKpiData({ totalItems, inStock, lowStock, needImmediate });
    };

    useEffect(() => {
        if (effectiveInventory.length > 0) {
            setKpiData(kpis);
        } else {
            fetchInventoryDataFromAPI();
            const intervalId = setInterval(fetchInventoryDataFromAPI, 2000);
            return () => clearInterval(intervalId);
        }
    }, [effectiveInventory, kpis]);

    // Format number with commas for thousands
    const formatNumber = (value) => {
        if (typeof value === 'number') {
            return value.toLocaleString();
        }
        return value;
    };

    // Toggle KPI visibility
    const toggleKPI = () => {
        setShowKPI(!showKPI);
    };

    // Determine if filters are active
    const isFiltered = filteredInventoryData && 
                      inventoryData && 
                      filteredInventoryData.length !== inventoryData.length &&
                      filteredInventoryData.length > 0;

    // Define the KPI cards data
    const kpiCards = [
        { 
            title: 'Total Items', 
            value: kpis.totalItems,
            icon: <BsInfoCircle />, 
            iconColor: '#f5f2e8'
        },
        { 
            title: 'In Stock', 
            value: kpis.inStock,
            icon: <BsCheckCircle />, 
            iconColor: '#e8f5e8'
        },
        { 
            title: 'Low Stock', 
            value: kpis.lowStock,
            icon: <BsHourglass />, 
            iconColor: '#fff8f0'
        },
        { 
            title: 'Need Stock Immediately', 
            value: kpis.needImmediate,
            icon: <BsXCircle />, 
            iconColor: '#fde8f4'
        }
    ];

    return (
        <>
            {/* Header with toggle button - COMMENTED OUT like PatientKPI */}
            {/* <div className="d-flex align-items-center justify-content-between mb-3 mt-4">
                <div className="d-flex align-items-center">
                    <h5 className="mb-0 me-3">Dashboard / Inventory</h5>
                    <button onClick={toggleKPI} className="btn text-primary p-0">
                        {showKPI ? 'Hide KPI ' : 'Show KPI '}
                        {showKPI ? <BsCaretDownFill size={12} /> : <BsCaretRightFill size={12} />}
                    </button>
                </div>
                {isFiltered && (
                    <div className="badge bg-info text-white">
                        Showing filtered data ({filteredInventoryData.length} of {inventoryData.length})
                    </div>
                )}
            </div> */}
            
            {showKPI && (
                <div className="row gy-3 introbx_row">
                    {kpiCards.map((kpi, index) => (
                        <div key={index} className="col">
                            <div className="d-flex align-items-center gap-2 p-3 rounded-10 shadow-sm">
                                <div 
                                    className="icon flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle" 
                                    style={{ 
                                        width: '2rem', 
                                        height: '2rem', 
                                        backgroundColor: '#E4E4FF', 
                                        color: '#8381FF' 
                                    }}
                                >
                                    <span className="d-block fs-lg">{kpi.icon}</span>
                                </div>
                                <div className="info flex-fill">
                                    <p className="fs-sm text-gray mb-0">{kpi.title}</p>
                                    <p className="lh-1 fw-semibold">{formatNumber(kpi.value)}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
}

export default Navigationmenu;